import { axios, path } from './config';
import qs from 'qs';
import {
  generateDateRangeFromTimeframe,
  convertSort,
  getAPIDateTimeFormat,
  generateExactDateRangeFromTimeframe,
} from './helpers';

import { pageNum } from 'appConstants';
const { ONE, TEN } = pageNum;

const DEFAULT_ITEMS_PER_PAGE = TEN;
const DEFAULT_PAGE = ONE;

export function statistics(userId) {
  const params = {
    userId,
  };
  return axios.get(path.STATISTICS, { params });
}

export function finalize({ fullName, password, companyName, phone, contactMe }) {
  const params = {
    full_name: fullName,
    password,
    name: companyName,
    phone,
    contact_me: contactMe,
  };
  return axios.get(path.FINALIZE, { params });
}

export function signup({
  firstName,
  lastName,
  companyName,
  email,
  password1,
  password2,
  areaCode,
  companySize,
  jobTitle,
  phone,
  method,
}) {
  const params = {
    new_username: email,
  };

  if (firstName) {
    params.first_name = firstName;
  }
  if (lastName) {
    params.last_name = lastName;
  }
  if (companyName) {
    params.name = companyName;
  }
  if (phone) {
    params.phone = phone;
  }
  if (password1) {
    params.new_password_1 = password1;
    params.new_password_2 = password2;
  }
  if (areaCode) {
    params.areacode = areaCode;
  }
  if (companySize) {
    params.company_size = companySize;
  }
  if (jobTitle) {
    params.job_title = jobTitle;
  }
  if (method) {
    params.method = method;
  }

  return axios.get(path.NEW_ACCOUNT, { params });
}

export function payInvoice({ invoiceId, token }) {
  const params = {
    id: invoiceId,
    card_token: token,
  };
  return axios.get(path.CUSTOMER_INVOICE_PAY, { params });
}

export function getInvoice(invoiceId) {
  const params = {
    id: invoiceId,
  };
  return axios.get(path.CUSTOMER_INVOICE, { params });
}

export function getEvents({
  filters,
  paging = {
    items_per_page: DEFAULT_ITEMS_PER_PAGE,
    page: DEFAULT_PAGE,
    sort: '+start',
  },
}) {
  const params = {};

  if (filters.dateRange && filters.dateRange.start) {
    params.date_range = generateExactDateRangeFromTimeframe(filters.dateRange);
  } else if (filters.timeframe && filters.timeframe.start) {
    params.date_range = generateExactDateRangeFromTimeframe(filters.timeframe);
  }
  params.date_cursor_direction = filters.dateCursorDirection;
  params.minimum_results_target = filters.minimumResultsTarget;

  params.items_per_page = paging.items_per_page;
  params.page = paging.page;
  params.sort = paging.sort;

  const queryFilter = [];
  if (filters.name) {
    queryFilter.push({ fieldName: 'name', type: 'Like', value: filters.name });
  }
  if (filters.tags) {
    queryFilter.push({ fieldName: 'fullTags.name', type: 'AllLike', value: filters.tags });
  }
  params.filter = JSON.stringify(queryFilter);
  params.status = filters.status;
  params.is_v2_event_type = true;

  return axios.get(path.EVENTS, { params });
}

export function generateEventsLink({ filters }) {
  const params = {};

  if (filters.timeframe && filters.timeframe.start) {
    params.date_range = generateDateRangeFromTimeframe(filters.timeframe);
  }
  const queryFilter = [];
  if (filters.name) {
    queryFilter.push({ fieldName: 'name', type: 'Like', value: filters.name });
  }
  if (filters.tags) {
    queryFilter.push({ fieldName: 'fullTags.name', type: 'AllLike', value: filters.tags });
  }
  if (filters.eventId) {
    queryFilter.push({ fieldName: 'id', type: 'Equals', value: filters.eventId });
  }
  params.filter = JSON.stringify(queryFilter);
  params.status = filters.status;
  params.items_per_page =
    filters.itemsPerPage === null ? DEFAULT_ITEMS_PER_PAGE : filters.itemsPerPage;
  params.page = filters.page;
  params.sort = convertSort(filters);

  return axios.get(path.GENERATE_EVENT_LINK, { params });
}

export function getEvent({ eventId, v2, globalEventId, startDate, endDate }) {
  const params = {
    event_id: eventId,
    v2,
    global_event_id: globalEventId,
    start_date: getAPIDateTimeFormat(startDate),
    end_date: getAPIDateTimeFormat(endDate),
  };

  return axios.get(path.GET_EVENT, { params });
}

export function addEvent({
  name,
  jobChatEnabled,
  startDate,
  endDate,
  timeZone,
  description,
  allDayEvent,
  maxCandidates,
}) {
  const params = {
    name,
    jobchat_enabled: jobChatEnabled || 'false',
    start_date: getAPIDateTimeFormat(startDate),
    timezone: timeZone,
    description,
    all_day_event: allDayEvent,
    max_candidates: maxCandidates,
  };

  if (endDate) {
    params.end_date = getAPIDateTimeFormat(endDate);
  }

  return axios.post(`event/add?${qs.stringify(params)}`);
}

export function addUpdateEventV2({
  name,
  visibility,
  jobChatEnabled,
  startDate,
  endDate,
  timeZone,
  description,
  allDayEvent,
  maxCandidates,
  recurrences,
  recurrenceType,
  userCalendarReminders,
  candidateCalendarReminders,
  addedParticipants,
  removedParticipants,
  eventId,
  globalEventId,
}) {
  const params = {
    name,
    event_visibility: visibility,
    jobchat_enabled: jobChatEnabled || 'false',
    start_date: getAPIDateTimeFormat(startDate),
    timezone: timeZone,
    description,
    all_day_event: allDayEvent,
    max_candidates: maxCandidates,
    recurrence_strings: JSON.stringify(recurrences),
    recurrence_type: recurrenceType,
    user_calendar_reminders: JSON.stringify(userCalendarReminders),
    candidate_calendar_reminders: JSON.stringify(candidateCalendarReminders),
    added_participants: JSON.stringify(addedParticipants),
    removed_participants: JSON.stringify(removedParticipants),
    event_id: eventId,
    global_event_id: globalEventId,
    is_v2_event_type: true,
  };

  if (endDate) {
    params.end_date = getAPIDateTimeFormat(endDate);
  }

  return axios.post(`event/add?${qs.stringify({ ...params })}`);
}

export function updateEvent({
  eventId,
  name,
  jobChatEnabled,
  startDate,
  endDate,
  timeZone,
  description,
  allDayEvent,
  maxCandidates,
}) {
  const params = {
    event_id: eventId,
    name,
    jobchat_enabled: jobChatEnabled || 'false',
    start_date: getAPIDateTimeFormat(startDate),
    timezone: timeZone,
    description,
    all_day_event: allDayEvent,
    max_candidates: maxCandidates,
  };

  if (endDate) {
    params.end_date = getAPIDateTimeFormat(endDate);
  }

  return axios.post(`event/update?${qs.stringify({ ...params })}`);
}

export function cancelEvent({ eventId }) {
  const params = {
    event_id: eventId,
    is_v2_event_type: true,
  };

  return axios.post(`event/delete?${qs.stringify({ ...params })}`);
}

export function eventParticipants({ eventId, participantType, omniSearch, itemsPerPage }) {
  const params = {
    event_id: eventId,
    participant_type: participantType,
    search: omniSearch,
    items_per_page: itemsPerPage,
  };

  return axios.get('events/participants', { params });
}
